/**
 * Custom hook for WebSocket chat functionality
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { getChatClient, ChatMessage, WebSocketMessage } from '../services/websocket.service';

export interface UseWebSocketReturn {
  messages: ChatMessage[];
  isConnected: boolean;
  isTyping: boolean;
  isLoading: boolean;
  error: string | null;
  sendMessage: (message: string, documentContext?: any) => void;
  clearMessages: () => void;
  connect: () => Promise<void>;
  disconnect: () => void;
}

export const useWebSocket = (autoConnect: boolean = true): UseWebSocketReturn => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const chatClient = useRef(getChatClient());
  const typingTimeoutRef = useRef<NodeJS.Timeout>();
  
  // Setup WebSocket event handlers
  useEffect(() => {
    const client = chatClient.current;
    
    // Handle incoming messages
    client.onMessage = (data: WebSocketMessage) => {
      if (data.type === 'typing') {
        handleTypingIndicator();
      } else if (data.type === 'message' && data.message) {
        setIsTyping(false);
        addMessage({
          id: `msg-${Date.now()}`,
          role: 'assistant',
          content: data.message,
          timestamp: new Date(),
          status: 'sent'
        });
      } else if (data.type === 'error') {
        setError(data.message || 'An error occurred');
        setIsTyping(false);
      }
    };
    
    // Handle connection events
    client.onConnect = () => {
      setIsConnected(true);
      setError(null);
      setIsLoading(false);
    };
    
    client.onDisconnect = (code, reason) => {
      setIsConnected(false);
      setIsTyping(false);
      setIsLoading(false);
      
      if (code === 1008) {
        setError('Authentication failed. Please sign in again.');
      } else if (code !== 1000) {
        setError(`Connection lost: ${reason || 'Unknown reason'}`);
      }
    };
    
    client.onError = (error) => {
      console.error('WebSocket error:', error);
      setError('Connection error. Please try again.');
      setIsLoading(false);
    };
    
    client.onTyping = () => {
      handleTypingIndicator();
    };
    
    // Auto-connect if requested
    if (autoConnect) {
      connect();
    }
    
    // Cleanup on unmount
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      // Don't disconnect here as it's a singleton
      // client.disconnect();
    };
  }, [autoConnect]);
  
  /**
   * Handle typing indicator with timeout
   */
  const handleTypingIndicator = useCallback(() => {
    setIsTyping(true);
    
    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Hide typing after 3 seconds if no new typing event
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 3000);
  }, []);
  
  /**
   * Connect to WebSocket
   */
  const connect = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      await chatClient.current.connect();
    } catch (error: any) {
      console.error('Failed to connect:', error);
      setError(error.message || 'Failed to connect to chat');
      setIsLoading(false);
    }
  }, []);
  
  /**
   * Disconnect from WebSocket
   */
  const disconnect = useCallback(() => {
    chatClient.current.disconnect();
    setIsConnected(false);
    setIsTyping(false);
  }, []);
  
  /**
   * Add message to the list
   */
  const addMessage = useCallback((message: ChatMessage) => {
    setMessages(prev => [...prev, message]);
  }, []);
  
  /**
   * Send message through WebSocket
   */
  const sendMessage = useCallback((content: string, documentContext?: any) => {
    if (!content.trim()) return;
    
    if (!chatClient.current.isConnected()) {
      setError('Not connected. Please wait...');
      return;
    }
    
    // Add user message to UI
    const userMessage: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: content,
      timestamp: new Date(),
      status: 'sending'
    };
    
    addMessage(userMessage);
    
    // Send through WebSocket
    try {
      chatClient.current.sendMessage(content, documentContext);
      
      // Update message status
      setMessages(prev => 
        prev.map(msg => 
          msg.id === userMessage.id 
            ? { ...msg, status: 'sent' } 
            : msg
        )
      );
    } catch (error) {
      console.error('Failed to send message:', error);
      setError('Failed to send message');
      
      // Update message status to error
      setMessages(prev => 
        prev.map(msg => 
          msg.id === userMessage.id 
            ? { ...msg, status: 'error' } 
            : msg
        )
      );
    }
  }, [addMessage]);
  
  /**
   * Clear all messages
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
  }, []);
  
  return {
    messages,
    isConnected,
    isTyping,
    isLoading,
    error,
    sendMessage,
    clearMessages,
    connect,
    disconnect
  };
};
